/**
 * viewportZoomPan — pure math for independent zoom/pan INSIDE an unlocked
 * paper-space viewport (MC4-interactive Slice 3).
 *
 * AutoCAD floating-MSPACE parity: after double-clicking into an unlocked
 * viewport, wheel-zoom and middle-button-pan change THAT viewport's model
 * view (its viewCenter / viewHeight, DXF group 12/22/45) instead of moving
 * the paper sheet. The active VP's scissor render then updates live from the
 * new view params (via buildViewportModelCamera in viewportRenderPass.ts).
 *
 * These helpers compute the new {viewCenter, viewHeight} for the
 * `geometry/viewport-set-view` command. They are framework-agnostic
 * (no THREE / DOM) so the zoom-around-cursor + pan math is unit-testable.
 *
 * # Zoom-around-cursor (twist-exact)
 *
 * Given the model point `cursorModel` currently under the cursor and a zoom
 * factor, we shrink/grow viewHeight and re-centre so `cursorModel` remains
 * under the cursor. Because the twist (rotation) is identical before and
 * after the zoom, the model-space relation
 *
 *     cursorModel − viewCenter = R(twist) · pRel · scale
 *
 * is linear in `scale = viewHeight / vpHeight`, so the re-centre formula
 *
 *     newViewCenter = cursorModel − (cursorModel − oldViewCenter) · (newH / oldH)
 *
 * holds EXACTLY for any twist angle (the rotation R cancels). No twist
 * approximation is needed for zoom.
 *
 * # Pan (twist-aware)
 *
 * A screen-pixel drag (dx, dy) maps to a model-space translation. The
 * model-units-per-screen-pixel factor is `viewHeight / vpScreenHeightPx`
 * (the VP's model view height divided by its on-screen pixel height). For a
 * twisted view the screen axes correspond to rotated model axes, so the
 * pixel delta is rotated by the twist angle to obtain the model delta — the
 * same +θ rotation `paperToModel` applies (viewport-local → model space).
 */

/** 2D point alias (matches viewportTransformAdapter.Point2D). */
export interface VpPoint2D {
  x: number;
  y: number;
}

export interface ViewportZoomResult {
  viewCenter: VpPoint2D;
  viewHeight: number;
}

/** Hard clamps so a runaway gesture cannot poison the view height. */
export const VP_MIN_VIEW_HEIGHT = 1e-9;
export const VP_MAX_VIEW_HEIGHT = 1e12;

/**
 * Compute the viewport's new {viewCenter, viewHeight} for a wheel-zoom that
 * keeps `cursorModel` (the model point currently under the cursor) pinned.
 *
 * FRAME-AGNOSTIC, frame-CONSISTENT: the formula uses only the DIFFERENCE
 * `cursorModel − oldViewCenter`, so it is correct in whichever model frame the
 * caller works in — provided BOTH arguments are in the SAME one. They are not
 * interchangeable today: `paperToModel` (hence `screenToModelCoords` and
 * `activeInputSpace.toSpace`) yields the **WCS** model point, while the
 * entity's stored `viewCenter` field is **DCS** (groups 12/22, whose origin is
 * `viewTarget`). The caller therefore feeds `viewportWcsViewCenter(vp)` here
 * and converts the WCS result back with `viewportStoredViewCenterFromWcs`
 * before writing the field — see `stageInteractions.onWheel`. This function
 * deliberately knows nothing about either helper.
 *
 * @param oldViewCenter current VP view centre, in the SAME frame as `cursorModel`
 * @param oldViewHeight current VP viewHeight (model units, > 0)
 * @param cursorModel   model point under the cursor (from screenToModelCoords)
 * @param zoomFactor    multiplicative factor from `wheelDeltaToZoomFactor`.
 *                      > 1 = zoom IN (deltaY < 0) → smaller viewHeight (more
 *                      magnified); < 1 = zoom OUT → larger viewHeight. Matches
 *                      the main camera's zoom direction so it feels identical.
 *
 * Twist-exact (see module header). Returns clamped viewHeight.
 */
export function zoomViewportAroundCursor(
  oldViewCenter: VpPoint2D,
  oldViewHeight: number,
  cursorModel: VpPoint2D,
  zoomFactor: number,
): ViewportZoomResult {
  // Zoom-in (factor > 1) shrinks the model height visible → more magnified,
  // matching zoomAroundCursor where ortho.zoom *= factor magnifies.
  const rawHeight = oldViewHeight / zoomFactor;
  const newViewHeight = Math.max(
    VP_MIN_VIEW_HEIGHT,
    Math.min(VP_MAX_VIEW_HEIGHT, rawHeight),
  );
  // Use the effective ratio AFTER clamping so the pin stays exact even when
  // the clamp engages.
  const ratio = oldViewHeight > 0 ? newViewHeight / oldViewHeight : 1;
  // newViewCenter = m − (m − oldViewCenter) · ratio  (per-axis, twist-exact).
  const newViewCenter: VpPoint2D = {
    x: cursorModel.x - (cursorModel.x - oldViewCenter.x) * ratio,
    y: cursorModel.y - (cursorModel.y - oldViewCenter.y) * ratio,
  };
  return { viewCenter: newViewCenter, viewHeight: newViewHeight };
}

/**
 * Compute the viewport's new viewCenter for an MMB pan of `(dxPx, dyPx)`
 * screen pixels.
 *
 * The model-units-per-screen-pixel factor is `viewHeight / vpScreenHeightPx`.
 * A screen drag to the right (dxPx > 0) should make the model FOLLOW the
 * drag (like the main-camera pan: world tracks the cursor), so viewCenter
 * moves LEFT in model space. CAD Y-up: screen dyPx (down-positive in client
 * coords) inverts so dragging down moves the view down.
 *
 * Twist-aware: the pixel delta is rotated by +twist (degrees) to map screen
 * axes → model axes, matching paperToModel's viewport-local → model rotation.
 *
 * FRAME: **WCS in, WCS out**. The `R(+twist)` above is exactly the screen→MODEL
 * axis map `paperToModel` applies, so the delta this returns is a WCS delta and
 * `oldViewCenter` must be the WCS window centre (`viewportWcsViewCenter`), not
 * the stored 12/22 pair. The result therefore goes back through
 * `viewportStoredViewCenterFromWcs` before it reaches the entity's DCS
 * `viewCenter` field — the same discipline as the zoom above.
 *
 * This used to be documented as "incremental, therefore frame-free in BOTH
 * directions", and it was, while DCS↔WCS was a pure TRANSLATION: a difference
 * is translation-invariant. It stopped being true when the view twist entered
 * that map (`wcs = target + R(twist)·dcs`, `ezdxf`-derived — see
 * `drawing-model/viewportViewFrame`): the two frames' AXES now differ, so
 * `Δstored = R(−twist)·Δwcs` and a WCS delta written straight into the DCS slot
 * double-rotates every twisted-viewport pan.
 *
 * @param oldViewCenter      current VP view centre, in **WCS** (see above)
 * @param viewHeight         current VP viewHeight (model units, > 0)
 * @param dxPx               screen-pixel delta X (client coords, right-positive)
 * @param dyPx               screen-pixel delta Y (client coords, down-positive)
 * @param vpScreenHeightPx   VP rectangle on-screen pixel height (> 0)
 * @param viewTwistAngleDeg  VP view twist (DXF group 51), degrees
 */
export function panViewportByPixels(
  oldViewCenter: VpPoint2D,
  viewHeight: number,
  dxPx: number,
  dyPx: number,
  vpScreenHeightPx: number,
  viewTwistAngleDeg: number,
): VpPoint2D {
  const safeScreenH = Math.max(vpScreenHeightPx, 1e-9);
  const modelPerPx = viewHeight / safeScreenH;
  // Screen delta in model units, before twist rotation. CAD Y-up → invert dy.
  const mDeltaX = dxPx * modelPerPx;
  const mDeltaY = -dyPx * modelPerPx;
  // Rotate by +θ (viewport-local → model space, same as paperToModel).
  const theta = (viewTwistAngleDeg * Math.PI) / 180;
  const cosT = Math.cos(theta);
  const sinT = Math.sin(theta);
  const rotDeltaX = mDeltaX * cosT - mDeltaY * sinT;
  const rotDeltaY = mDeltaX * sinT + mDeltaY * cosT;
  // viewCenter moves OPPOSITE the drag so the model follows the cursor.
  return {
    x: oldViewCenter.x - rotDeltaX,
    y: oldViewCenter.y - rotDeltaY,
  };
}
